'use client';

import * as React from 'react';
import {useEffect, useState} from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from './lib/utils';

/**
 * Height of the invisible drag strip at the top of a `handleOnly` sheet, and the space reserved
 * for it. 24px was not enough to find or hold with a thumb, and on the dealership sheet the
 * fixed footer bar covered all but 15px of it at the peek stop, so the sheet could be pushed
 * down and never grabbed again. 44px is the usual minimum touch target.
 */
const GRAB_BAND_PX = 44;
type DrawerDirection = 'top' | 'bottom' | 'left' | 'right';

const Drawer = ({
  open,
  onClose,
  snapPoints,
  direction = 'bottom',
  keepMounted = false,
  dismissible = true,
  modal = true,
  snapToPoints = false,
  handleOnly = false,
  activeSnapPoint,
  // Default true preserves prior vaul behavior for form drawers. Territory map
  // sheets pass false so iOS does not raise the keyboard and shrink the WebView.
  autoFocus = true,
  ...props
}: {
  open?: boolean;
  onClose?: () => void;
  direction?: DrawerDirection;
  keepMounted?: boolean;
  children?: React.ReactNode;
  dismissible?: boolean;
  modal?: boolean;
  // vaul reads a *string* snap point as pixels (`parseInt`, so '45%' silently means 45px)
  // and a *number* as a fraction of the container height. Prefer numbers.
  snapPoints?: (string | number)[];
  snapToPoints?: boolean;
  // Only the drag handle starts a drag. Without it vaul claims pointer-downs anywhere on
  // the content, so on a sheet with snap points a swipe over a scrollable list drags the
  // sheet instead of scrolling it. Needs a real DrawerPrimitive.Handle to exist.
  handleOnly?: boolean;
  activeSnapPoint?: number | string | null; // Allow activeSnapPoint to be passed in
  autoFocus?: boolean;
}) => {
  const snapPointsValues = snapPoints ? [...snapPoints, 1] : undefined;
  const [snap, setSnap] = useState<number | string | null>(
      activeSnapPoint ? activeSnapPoint : 1
  );
  const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (activeSnapPoint !== undefined && activeSnapPoint !== null && activeSnapPoint !== snap) {
            setSnap(activeSnapPoint);
        }
    } , [activeSnapPoint]);

  if (!keepMounted && !open) {
    return null;
  }
  return (
    <DrawerPrimitive.Root
      direction={direction}
      open={open}
      autoFocus={autoFocus}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setIsClosing(true);
        }
      }}
      onAnimationEnd={() => {
        setIsClosing(false);
        onClose?.();
      }}
      dismissible={dismissible}
      snapPoints={snapPointsValues}
      activeSnapPoint={snap}
      snapToSequentialPoint={snapToPoints}
      handleOnly={handleOnly}
      setActiveSnapPoint={setSnap}
      modal={modal}
      {...props}
    >
      {React.Children.map(props.children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            direction,
            snapPoints,
            isClosing,
            // don't clobber a value the consumer set on DrawerContent directly
            handleOnly: (child.props as any)?.handleOnly ?? handleOnly,
          } as any);
        }
        return child;
      })}
    </DrawerPrimitive.Root>
  );
};
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
    isClosing?: boolean;
  }
>(({ className, isClosing, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[1000] transition-all duration-300',
      !isClosing ? 'bg-black/60' : 'bg-black/0',
      className
    )}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    direction?: DrawerDirection;
    handle?: boolean;
    fullWidth?: boolean;
    size?: string | number;
    title?: string;
    description?: string;
    snapPoints?: (string | number)[];
    isClosing?: boolean;
    handleOnly?: boolean;
  }
>(
  (
    {
      className,
      children,
      direction = 'bottom',
      handle = true,
      fullWidth = false,
      size,
      title = 'dialog',
      description = 'description',
      snapPoints,
      isClosing,
      handleOnly = false,
      ...props
    },
    ref
  ) => {
    const sizeValue = size
      ? typeof size === 'number'
        ? size > 100
          ? `${size}px`
          : size < 1
          ? `${size * 100}%`
          : `${size}%`
        : typeof size === 'string' && size.includes('/')
        ? `${eval(size) * 100}%`
        : typeof size === 'string' && /^[0-9]*\.?[0-9]+$/.test(size)
        ? parseFloat(size) > 100
          ? `${size}px`
          : parseFloat(size) < 1
          ? `${parseFloat(size) * 100}%`
          : `${size}%`
        : size
      : undefined;

    return (
      <DrawerPortal>
        <DrawerOverlay isClosing={isClosing} />
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            'fixed z-[2000] flex flex-col border-0 bg-background transition-transform duration-75',
            {
              'inset-x-0 bottom-0': direction === 'bottom',
              'inset-x-0 top-0': direction === 'top',
              'inset-y-0 right-0': direction === 'right',
              'inset-y-0 left-0': direction === 'left',
              'translate-y-full': direction === 'bottom' && isClosing,
              '-translate-y-full': direction === 'top' && isClosing,
              'translate-x-full': direction === 'right' && isClosing,
              '-translate-x-full': direction === 'left' && isClosing,
              'h-auto':
                (direction === 'bottom' || direction === 'top') && !fullWidth,
              'h-full':
                direction === 'left' ||
                direction === 'right' ||
                ((direction === 'bottom' || direction === 'top') && fullWidth),
              'w-full': direction === 'left' || direction === 'right',
              'sm:w-96':
                (direction === 'left' || direction === 'right') &&
                !sizeValue &&
                !fullWidth &&
                snapPoints === undefined,
            },
            sizeValue && {
              'w-full sm:w-[var(--drawer-size)]':
                direction === 'left' || direction === 'right',
              'h-full sm:h-[var(--drawer-size)]':
                direction === 'top' || direction === 'bottom',
            },
            className
          )}
          style={
            {
              '--drawer-size': sizeValue,
              '--vaul-drawer-duration': '100ms',
              // vaul slides a fixed-height sheet down to reach a snap point, so at any snap
              // below full its bottom hangs `offset` px past the viewport - and the scroll
              // container goes with it, leaving the end of the list unreachable however far you
              // scroll. vaul publishes that offset as --snap-point-height, so padding the sheet
              // by it makes the content box exactly the *visible* region at every snap, and the
              // flex-1 list below gets a height it can actually scroll to the end of.
              // Unset (no snap points) falls back to 0px, so ordinary drawers are unaffected.
              paddingBottom: 'var(--snap-point-height, 0px)',
              // Reserve the grab band. Without it the strip overlaps whatever the sheet puts at
              // its top - on the route sheet the header starts 16px in, so a 44px strip would
              // swallow the Edit button - and the header, painting later, would win anyway,
              // leaving the strip only those 16px to be grabbed by.
              // Reserve the band. A top-direction sheet has to fold it into paddingBottom,
              // which already carries the snap offset, or one would overwrite the other.
              ...(handleOnly && direction === 'bottom' ? { paddingTop: GRAB_BAND_PX } : {}),
              ...(handleOnly && direction === 'top'
                ? { paddingBottom: `calc(var(--snap-point-height, 0px) + ${GRAB_BAND_PX}px)` }
                : {}),
            } as React.CSSProperties
          }
          {...props}
        >
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          {description && (
            <DrawerDescription className="sr-only">
              {description}
            </DrawerDescription>
          )}
          {/* vaul only begins a drag from `data-vaul-handle` when `handleOnly` is set, and that
              element has to be its own Handle component (it carries the pointer handlers, not
              just the attribute). Styling it directly does not work: vaul injects *unlayered*
              CSS for `[data-vaul-handle]` (position: relative, width: 32px, margin: auto),
              which beats Tailwind utilities in `@layer utilities` - the pill came out 32px wide
              and off-centre. So the Handle is an invisible full-width strip across the top,
              forced with inline styles (which do beat unlayered CSS), and the visible pill below
              is left exactly as it was. The strip is also a much bigger drag target than a
              100x6 pill. Bottom/top sheets only - a side drawer's top edge is ordinary content. */}
          {handle && (direction === 'bottom' || direction === 'top') && (
            <DrawerPrimitive.Handle
              aria-label="Resize"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                [direction === 'bottom' ? 'top' : 'bottom']: 0,
                width: '100%',
                maxWidth: 'none',
                height: GRAB_BAND_PX,
                margin: 0,
                background: 'transparent',
                borderRadius: 0,
              }}
            />
          )}
          {handle && (
            <div
              className={cn(
                // pointer-events-none so a drag starting on the pill reaches the Handle strip
                // underneath it rather than being swallowed here.
                'pointer-events-none',
                'absolute h-2 w-[100px] rounded-full opacity-90 bg-muted-foreground/40',
                {
                  'left-1/2 -translate-x-1/2 top-1.5':
                    direction === 'bottom' && !fullWidth,
                  'left-1/2 -translate-x-1/2 top-2':
                    direction === 'bottom' && fullWidth,
                  'left-1/2 -translate-x-1/2 bottom-1.5':
                    direction === 'top' && !fullWidth,
                  'left-1/2 -translate-x-1/2 bottom-2':
                    direction === 'top' && fullWidth,
                  [`hidden sm:block
                  } right-[325px] top-1/2 -translate-y-1/2 rotate-90`]:
                    direction === 'right' && !fullWidth,
                  '-left-9 top-1/2 -translate-y-1/2 rotate-90':
                    direction === 'right' && fullWidth,
                  [`hidden sm:block left-[325px] top-1/2 -translate-y-1/2 rotate-90`]:
                    direction === 'left' && !fullWidth,
                  '-right-9 top-1/2 -translate-y-1/2 rotate-90':
                    direction === 'left' && fullWidth,
                }
              )}
              style={
                sizeValue && !fullWidth
                  ? {
                      [direction === 'left' || direction === 'right'
                        ? direction === 'left'
                          ? 'left'
                          : 'right'
                        : direction === 'top'
                        ? 'bottom'
                        : 'top']:
                        direction === 'left' || direction === 'right'
                          ? `calc(100% - 58px)`
                          : '.3rem',
                    }
                  : undefined
              }
            />
          )}
          {children}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  }
);
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        'flex w-full p-4 text-center sm:text-left before:absolute before:top-0 before:bottom-0 before:right-[-100vw] before:left-0 before:-z-10',
        className
      )}
      {...props}
    />
  );
};
DrawerHeader.displayName = 'DrawerHeader';

// className={cn(`grid gap-1.5 p-4 text-center sm:text-left`, className)}

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

const DrawerNested = ({
  shouldScaleBackground = true,
  open,
  onClose,
  direction = 'bottom',
  dismissible = true,
  modal = true,
  snapToPoints = false,
  snapPoints,
  ...props
}: {
  shouldScaleBackground?: boolean;
  open?: boolean;
  onClose?: () => void;
  direction?: DrawerDirection;
  children?: React.ReactNode;
  dismissible?: boolean;
  modal?: boolean;
  snapPoints?: (string | number)[];
  snapToPoints?: boolean;
  keepMounted?: boolean;
}) => {
  const snapPointsValues = snapPoints ? [...snapPoints, 1] : undefined;
  const [snap, setSnap] = useState<number | string | null>(
    snapPoints ? snapPoints[0] : 1
  );
  const [isClosing, setIsClosing] = useState(false);

  return (
    <DrawerPrimitive.NestedRoot
      direction={direction}
      autoFocus
      shouldScaleBackground={shouldScaleBackground}
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose?.();
        }
      }}
      onAnimationEnd={() => {
        setIsClosing(false);
        onClose?.();
      }}
      dismissible={dismissible}
      snapPoints={snapPointsValues}
      activeSnapPoint={snap}
      snapToSequentialPoint={snapToPoints}
      setActiveSnapPoint={setSnap}
      modal={modal}
      {...props}
    >
      <DrawerPortal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-[1000] bg-black/60" />
      </DrawerPortal>
      {React.Children.map(props.children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { direction } as any);
        }
        return child;
      })}
    </DrawerPrimitive.NestedRoot>
  );
};
DrawerNested.displayName = 'DrawerNestedRoot';

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  DrawerNested,
};
